import { isString, isBoolean, isArray } from './type-guards';

/**
 * Generic validation rule interface
 */
export interface ValidationRule {
  required: boolean;
  validate: (value: unknown, ...args: unknown[]) => string | undefined;
  minLength?: number;
  minValue?: number;
  minItems?: number;
}

/**
 * Generic validation rules type
 */
export type ValidationRules<T extends Record<string, unknown>> = {
  [K in keyof T]: ValidationRule;
};

/**
 * Generic validation result interface
 */
export interface ValidationResult<T extends Record<string, unknown>> {
  isValid: boolean;
  errors: Partial<Record<keyof T, string>>;
}

/**
 * Generic form errors type
 */
export type FormErrors<T extends Record<string, unknown>> = Partial<Record<keyof T, string>>;

/**
 * Validate a single field using the configured validation rules
 */
export const validateField = <T extends Record<string, unknown>>(
  field: keyof T,
  value: unknown,
  data: T,
  rules: ValidationRules<T>,
): string | undefined => {
  const rule = rules[field];
  if (!rule) return undefined;

  return rule.validate(value, data);
};

/**
 * Determine if a field should be validated based on its context.
 * This is a generic implementation that can be customized per form.
 */
export const isFieldRequired = <T extends Record<string, unknown>>(
  field: keyof T,
  data: T,
  rules: ValidationRules<T>,
  customRequiredLogic?: (field: keyof T, data: T) => boolean,
): boolean => {
  const rule = rules[field];
  if (!rule) return false;

  // If custom logic is provided, use it
  if (customRequiredLogic) {
    return customRequiredLogic(field, data);
  }

  // Default logic: field is required if rule.required is true
  return rule.required === true;
};

/**
 * Generic form validation function
 */
export const validateForm = <T extends Record<string, unknown>>(
  data: T,
  rules: ValidationRules<T>,
  customRequiredLogic?: (field: keyof T, data: T) => boolean,
): ValidationResult<T> => {
  const errors: FormErrors<T> = {};
  const fieldsToValidate: (keyof T)[] = Object.keys(data) as Array<keyof T>;

  fieldsToValidate.forEach((field) => {
    const rule = rules[field];
    if (!rule) return;

    const isRequired = isFieldRequired(field, data, rules, customRequiredLogic);
    const hasValue = data[field] != null;

    if (isRequired || (rule.required === false && hasValue)) {
      const error = validateField(field, data[field], data, rules);
      if (error) {
        errors[field] = error;
      }
    }
  });
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Validate a single field and return its error
 */
export const validateSingleField = <T extends Record<string, unknown>>(
  field: keyof T,
  value: unknown,
  data: T,
  rules: ValidationRules<T>,
  customRequiredLogic?: (field: keyof T, data: T) => boolean,
): string | undefined => {
  const rule = rules[field];
  if (!rule) return undefined;

  const isRequired = isFieldRequired(field, data, rules, customRequiredLogic);
  const hasValue = value != null;

  // Only validate if field is required or has a value
  if (isRequired || (rule.required === false && hasValue)) {
    return validateField(field, value, data, rules);
  }

  return undefined;
};

/**
 * Common validation functions that can be reused across forms
 */
export const commonValidators = {
  requiredString: (
    value: unknown,
    errorMessage: string = 'This field is required',
  ): string | undefined => {
    if (!isString(value)) {
      return errorMessage;
    }
    if (!value.trim()) {
      return errorMessage;
    }
    return undefined;
  },
  optionalString: (value: unknown): string | undefined => {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (!isString(value)) {
      return 'Must be a string';
    }
    return undefined;
  },
  stringWithMinLength: (
    value: unknown,
    minLength: number,
    errorMessage?: string,
  ): string | undefined => {
    if (!isString(value)) {
      return errorMessage || 'Must be a string';
    }
    if (!value.trim()) {
      return errorMessage || 'This field is required';
    }
    if (value.trim().length < minLength) {
      return errorMessage || `Must be at least ${minLength} characters long`;
    }
    return undefined;
  },
  requiredBoolean: (
    value: unknown,
    errorMessage: string = 'This field is required',
  ): string | undefined => {
    if (!isBoolean(value)) {
      return errorMessage;
    }
    return undefined;
  },
  requiredArray: (
    value: unknown,
    errorMessage: string = 'At least one item is required',
  ): string | undefined => {
    if (!isArray(value) || value.length === 0) {
      return errorMessage;
    }
    return undefined;
  },
  optionalArray: (value: unknown): string | undefined => {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (!isArray(value)) {
      return 'Must be an array';
    }
    return undefined;
  },
  positiveNumber: (value: unknown, errorMessage?: string): string | undefined => {
    if (value === null || value === undefined) {
      return undefined; // Optional field
    }
    if (typeof value !== 'number' || isNaN(value)) {
      return errorMessage || 'Must be a valid number';
    }
    if (value < 0) {
      return errorMessage || 'Must be a positive number';
    }
    return undefined;
  },
  positiveInteger: (value: unknown, errorMessage?: string): string | undefined => {
    if (value === null || value === undefined) {
      return undefined; // Optional field
    }
    if (typeof value !== 'number' || isNaN(value)) {
      return errorMessage || 'Must be a valid number';
    }
    if (value < 1) {
      return errorMessage || 'Must be greater than 0';
    }
    if (!Number.isInteger(value)) {
      return errorMessage || 'Must be a whole number';
    }
    return undefined;
  },
  domainFormat: (value: unknown, errorMessage?: string): string | undefined => {
    if (!isString(value)) {
      return errorMessage || 'Must be a string';
    }
    if (value.trim() && !/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value.trim())) {
      return errorMessage || 'Invalid domain format';
    }
    return undefined;
  },
  enumValue: <T extends string>(
    value: unknown,
    validValues: readonly T[],
    errorMessage?: string,
  ): string | undefined => {
    if (!isString(value)) {
      return errorMessage || 'Must be a string';
    }
    if (!value.trim()) {
      return errorMessage || 'This field is required';
    }
    if (!validValues.includes(value as T)) {
      return errorMessage || 'Please select a valid option';
    }
    return undefined;
  },
};
